import { TBriteVerifyResponse } from '../hooks/useBriteVerify';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import SessionStorage from './SessionStorage';
import { acquisitionApiRequest, AcquisitionInitResponse } from './acquisition-api/AcquisitionApi';
import { callAcquisitionBff } from './acquisition-api/AcquisitionBff';
import { API_MAPPING } from './api-utils/api-url-map';
import { cleanInternalLinkUrl, regexValidation, calculateAge } from './cob-utils';
import axios from 'axios';
import { FunctionPipe, ValidationResult } from './FunctionPipe';
import { TAcquisitionProviderFacade } from '../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';



export const homeAddressCheckRegex =
    /(p\.?\s?(o|b)\.?(\s?(box|drawer|vox))?|post\soffice\s(box)?|p\/o\sbox|cluster\sbox|box)(\s?\d+)|general\sdelivery/i;

export const getAppData = async (): Promise<Record<string, any>> => {
    if (SessionStorage.verifyNotEmpty('prequalFullAppData')) {
        return await SessionStorage.getCipher('prequalFullAppData', true);
    }
    if (SessionStorage.verifyNotEmpty('preApprovalFullAppData')) {
        return await SessionStorage.getCipher('preApprovalFullAppData', true);
    }
    // New API's
    if (SessionStorage.verifyNotEmpty('flowApplicationData')) {
        return SessionStorage.getObject('flowApplicationData');
    }
    return {};
};

export const getPersonalInfo = async (): Promise<Record<string, any>> => {
    if (SessionStorage.verifyNotEmpty('personalInfo')) {
        return await SessionStorage.getCipher('personalInfo', true);
    }
    return {};
};

export const formatDateOfBirth = (dateOfBirth: string): string => {
    const [month, day, year] = dateOfBirth.split('/');
    return `${Number(month)}/${Number(day)}/${Number(year)}`;
};

export const areStringsEqual = (stringOne: string, stringTwo: string) => {
    if (stringOne && stringTwo && typeof (stringOne) === 'string' && typeof (stringTwo) === 'string') {
        if (stringOne.toLowerCase() === stringTwo.toLowerCase()) {
            return true;
        }
    }
    return false;
};

export const enforceTwoDigitDOB = (value: number | string): string => {
    if (typeof value === 'number') {
        if (value === 0) {
            return '';
        }
        return value < 10 ? ('0' + value) : ('' + value);
    } else if (typeof value === 'string') {
        return value.length === 1 ? `0${value}` : value;
    }
};

export const getNameAndDOBFromSessionStorage = async (appSecondPage?: boolean): Promise<any> => {
    const isOnVerificationPage:boolean =  window.location.href.includes('verification');
    const isOnAuthorMode:boolean =  window.location.host.includes('4502');
    const currentFlow = getAcquisitionFlow();

    let appDataFirstName = '';
    let appDataLastName = '';
    let appDataDOB = '';

    if (isOnAuthorMode) {
        return [appDataFirstName, appDataLastName, appDataDOB];
    }

    // Only PQ is checked on the application page
    if (currentFlow === AcquisitionFlow.PQ) {
        const pqAppData =  await getPersonalInfo();
        appDataFirstName = pqAppData.FirstName;
        appDataLastName = pqAppData.LastName;
        appDataDOB = (enforceTwoDigitDOB(pqAppData.DOBMonth)) + '/' + (enforceTwoDigitDOB(pqAppData.DOBDay)) + '/' + pqAppData.DOBYear;
    // all other flows are only checked on verification page or if split over 2 pages
    } else if (isOnVerificationPage || appSecondPage) {
        if (currentFlow === AcquisitionFlow.PA) {
            let paAppData =  await getAppData();
            if (!paAppData) {
                paAppData = await SessionStorage.tryGetObjectOrCipher('pre-approval-data', {});
            }
            appDataFirstName = paAppData.Applicant?.ApplicantName?.FirstName ?? '';
            appDataLastName = paAppData.Applicant?.ApplicantName?.LastName ?? '';
            const dateOfBirthString: string = paAppData.Applicant?.DateOfBirth ?? '';
            const arrayOfDate = dateOfBirthString.split('/');
            if (arrayOfDate.length === 3) {
                appDataDOB = arrayOfDate[0] + '/' + arrayOfDate[1] + '/' + arrayOfDate[2];
            }
        } else if (currentFlow === AcquisitionFlow.SkipPQ || currentFlow === AcquisitionFlow.PqOfOne) {
            const appData = await getAppData();
            appDataFirstName = appData?.PersonalInformation?.FirstName ?? '';
            appDataLastName = appData?.PersonalInformation?.LastName ?? '';
            appDataDOB = appData?.PersonalInformation?.DOBMonth + '/' + appData?.PersonalInformation?.DOBDay + '/' + appData?.PersonalInformation?.DOBYear;
        } else if (currentFlow === AcquisitionFlow.PHA) {
            // any used to resolve immediately used nested types
            const personalInfo = (await SessionStorage.tryGetCipher<any>('flowApplicationData', {}))?.parties?.[0];
            appDataFirstName = personalInfo?.firstName;
            appDataLastName = personalInfo?.lastName;
    
            const dateParts = personalInfo?.dateOfBirth?.split('-');
            const birthDay = dateParts?.[2] ?? '';
            const birthMonth = dateParts?.[1] ?? '';
            const birthYear = dateParts?.[0] ?? '';
            appDataDOB = `${birthMonth}/${birthDay}/${birthYear}`;
        }
    }

    return [appDataFirstName, appDataLastName, appDataDOB];
};

export const getProgramData = async (): Promise<Record<string, any>> => {
    if (SessionStorage.verifyNotEmpty('prequalResponse')) {
        const selectedProgramCode = SessionStorage.get('selectedProgramCode');
        const prequalResponse = await SessionStorage.tryGetCipher<Record<string,any>>('prequalResponse', {}); // Any used to resolve legacy nested typing issues
        const appArray = prequalResponse.ApplicationInformation
            ? prequalResponse.ApplicationInformation
            : [];
        for (const app of appArray) {
            if (
                app.ProgramRates &&
                app.ProgramRates.ProgramCode === selectedProgramCode
            ) {
                return app;
            }
        }
        return {};
    }

    // New API's
    if (SessionStorage.verifyNotEmpty('applicationOfferResponse')) {
        return SessionStorage.tryGetObject('applicationOfferResponse', {});
    }

    return await SessionStorage.tryGetCipher('pre-approval-data', {});
};

export const showCardTooltip = (tooltipMap: string[], cardCode: string, programCode: string): boolean => {
    for (const mapping of tooltipMap) {
        const jsonMap = JSON.parse(mapping);
        if (jsonMap.keyValues1_programCode.includes(programCode)) {
            for (const cardId of jsonMap.keyValues1_ecmf) {
                if (cardId.keyValues1_ecmf_cardid === cardCode) {
                    return true;
                }
            }
        }
    }
    return false;
};

export const formatProperNames = (val: string): string => {
    if (!val) {
        return '';
    }
    let formatted = val.trim().toLowerCase();
    if (formatted.length < 1) {
        return formatted;
    }
    formatted = formatted[0].toUpperCase() + formatted.substring(1);
    formatted = formatted
        .replace(/\s\s/g, ' ')
        .replace(/\s(\S)\s/g, (_match: string, p1: string) => {
            return ' ' + p1.toUpperCase() + '. ';
        })
        .replace(/\s(\S)\S/g, (match: string, p1: string) => {
            return ' ' + p1 + match.substring(2);
        });
    return formatted;
};

export const isPopularCardShown = (parsedPopCard, card) => {
    if (card.premiumFee === 'Free') {
        return false;
    }
    const getKeyByValue = (object, value, regex?)  => {
        return regex ? Object.keys(object).find(key => regex.test(key) && object[key]) :
            Object.keys(object).find(key => object[key] === value[0]);
    };

    const acqMap = {
        PQ: 'pre-qualification',
        PA: 'pre-approved',
        SkipPQ: 'full-application',
        PQOne: 'pq-one',
    };

    const flows = Object.values(acqMap).map((value) => value);
    const currentWindow = flows.filter((flow: string) => new RegExp(flow).test(window.location.href));
    const flowKey = getKeyByValue(acqMap, currentWindow);

    const term = 'keyValues3_isSuppressed' + flowKey;
    const regex = new RegExp(`\\b${term}\\b`);
    const isSuppressingCard = getKeyByValue(parsedPopCard, flowKey, regex) ? false : true;
    return isSuppressingCard;
};

const attributes = [
    'C1BSourceID',
    'C1BDescriptorID',
    'C1BSpecificationID',
    'C1BPartnerID',
    'AID',
    'DF1',
    'DF2',
    'DF3', // Used to track Marketing Campaign Code
    'DF4', // Sometimes used for Target
] as const;
// Developer Note: These are notated by our use in DPM WE, but are not restricted on the backend.
//   Any key may end up being used by transactional without notice
export type TAcquisitionsMarketingAttributeArray = {Name: typeof attributes[number], Value: string}[];

export const getMarketingAttributesForAcquisitions = (): TAcquisitionsMarketingAttributeArray  => {
    const toReturnMarketingAttributes: {[key in typeof attributes[number]]?: string } = {};

    const existingAttributes = SessionStorage.tryGetObject('MarketingAttributes', {});

    for (const entry in existingAttributes) {
        toReturnMarketingAttributes[entry as typeof attributes[number]] = existingAttributes[entry];
    }

    const params = new URLSearchParams(window.location.search);
    if (Array.from(params).length > 0) { // Array.from() is used for compatibility with pre-2023 browser releases
        for (const [key, value] of params) {
            toReturnMarketingAttributes[key as typeof attributes[number]] = value;
        }
    }

    const marketingAttributesArray = Object.entries(toReturnMarketingAttributes) as [typeof attributes[number], string][];
    return marketingAttributesArray.map(( [Name, Value] ) => ({ Name, Value }) );
};

export const getMarketingAttributes = () => {

    const nextMarketingAttributes = {} as { [key: string]: string};

    const existingAttributes = SessionStorage.tryGetObject('MarketingAttributes', {});
    if (Array.isArray(existingAttributes)) {
        for (const { Name, Value } of (existingAttributes as TAcquisitionsMarketingAttributeArray)) {
            nextMarketingAttributes[Name] = Value;
        }
    } else {
        for (const entry in existingAttributes) {
            nextMarketingAttributes[entry] = existingAttributes[entry];
        }
    }

    const params = new URLSearchParams(window.location.search);
    if (Array.from(params).length > 0) { // Array.from() is used for compatibility with pre-2023 browser releases
        for (const [key, value] of params) {
            nextMarketingAttributes[key] = value;
        }
    }

    SessionStorage.setObject('MarketingAttributes', nextMarketingAttributes);

    return nextMarketingAttributes;
};

export const clearSessionStorageVariablesForAcquisitionFlow = () => {
    SessionStorage.removeItems([
        'acctId',
        'afterCounterOffer',
        'app-approved-data',
        'app-verification',
        'appAuthorizedResponse',
        'ApplicationId',
        'backIdImageData',
        'barcodeData',
        'bvrslt',
        'cardInfo',
        'creditLine',
        'dataManagerProgramCode',
        'deIndexType',
        'flowApplicationData',
        'flowData',
        'frontIdImageData',
        'fullAppResponse',
        'fundingResponse',
        'hasError',
        'idVerified',
        'initResponse',
        'isCounterOffer',
        'isMobileOnlyApplication',
        'isSkipPreCounterOffer',
        'isSkipPreQual',
        'lastVisitedPage',
        'levelThreeResponse',
        'mitekResults',
        'offerContent',
        'passportImageData',
        'personalInfo',
        'pqOfOneAuthorizedUserShowing',
        'pre-approval-data',
        'preApprovalFullAppData',
        'prequalFullAppData',
        'prequalResponse',
        'previousVisitedPage',
        'protocol',
        'queryParams',
        'selectedCardImgPath',
        'selectedProgramCode',
        'selfieImageData',
        'sessionId',
        'temp2',
        'token',
        'persistingFlowApplicationData',
    ]);
};

export const callDataExposureApi = (
    flow?: AcquisitionFlow,
): Promise<boolean> => {

    //DPM-13110 -- Do not call Exposure API if feature flag is set
    const featureFlags: Record<string, boolean> = JSON.parse(
        PathUtils.getMetaPropertyValue('featureFlagsProvider') ||
            '{}',
    );
    if (featureFlags && featureFlags['DPM-13110'] === true) {
        return Promise.resolve(true);
    }

    const endpointsNames: AcquisitionFlowMap = {
        preQual: 'pq-data-exposure',
        preApproved: 'pa-data-exposure',
        skipPQ: 'skip-pq-data-exposure',
        pqOne: 'skip-pq-data-exposure',
        partnerHosted: '',
        [AcquisitionFlow.SkipPQCounter]: ''
    };

    if (!flow) {
        flow = getAcquisitionFlow();
    }

    const apiName = endpointsNames[flow];

    return new Promise(resolve => {
        acquisitionApiRequest<{
            ApplicationId: string;
            ReservationNumber: string;
        }>(apiName as keyof typeof API_MAPPING, { method: 'GET' })
            .then(response => {
                if (response.status !== 200) {
                    throw response;
                }
                SessionStorage.set(
                    'ApplicationId',
                    response.data.ApplicationId,
                );
                SessionStorage.set(
                    'ReservationNumber',
                    response.data.ReservationNumber,
                );
                resolve(true);
            })
            .catch(error => {
                if (error && error.status === 408) {
                    resolve(false);
                } else {
                    resolve(true);
                }
            });
    });
};

export const enum AcquisitionFlow {
    PQ = 'preQual',
    SkipPQ = 'skipPQ',
    SkipPQCounter = 'skipPQ-CounterOffer',
    PqOfOne = 'pqOne',
    PA = 'preApproved',
    PHA = 'partnerHosted'
}

export const checkIfInValidAcquisitionFlow = (validFlows: AcquisitionFlow[], flowToCheck: AcquisitionFlow = getAcquisitionFlow()) => {
    try {
        for (const validFlow of validFlows) {
            if (flowToCheck.includes(validFlow)) {
                return true;
            }
        }
    } catch (ignored) {
        console.error('Unable to validate flow.');
    } 
    return false;
};

export type AcquisitionFlowMap = {[key in AcquisitionFlow]: string};

export const getAcquisitionFlow = (): AcquisitionFlow => {
    const path = window.location.pathname;
    if (path.search('/pre-approved') > -1) {
        return AcquisitionFlow.PA;
    } else if (path.search('/full-application') > -1) {
        if (SessionStorage.get('isCounterOffer') === 'true') {
            return AcquisitionFlow.SkipPQCounter;
        } else {
            return AcquisitionFlow.SkipPQ;
        }
    } else if (path.search('/pq-one') > -1) {
        return AcquisitionFlow.PqOfOne;
    } else if (path.search('/pha-secured') > -1) {
        return AcquisitionFlow.PHA;
    }
    return AcquisitionFlow.PQ;
};

export const getCurrentlyInAcquisitionFlow = (): boolean => {
    return /(pre-qualification|pre-approved|full-application|pq-one|pha-secured)/g.test(window.location.href);
};

export const getDataEntryPages = () => ({
    contextPath: '/content/cob-corp-acquisition/us/en',
    preQualDataEntryPageUrl: '/pre-qualification/data-entry/index.html',
    skipPreQualDataEntryPageUrl: '/full-application/data-entry/index.html',
    skipPreQualCounterDataEntryPageUrl:
    '/full-application/application/counter-application.html',
    preApprovalDataEntryPageUrl: '/pre-approved/data-entry/index.html',
    pqOfOneDataEntryPageUrl: '/pre-qualification/data-entry/index.html',
    partnerHostedDataEntryPageUrl: '/pre-qualification/data-entry/index.html',
});

const extractAndMergeParams = (redirectWithParams?: string): {params: URLSearchParams, redirect?: string} => {
    const params = new URLSearchParams(window.location.search);
    if (redirectWithParams && redirectWithParams.indexOf('?') > -1) {
        const redirectParams = new URLSearchParams(redirectWithParams.substring(redirectWithParams.indexOf('?')));
        for (const p of redirectParams) {
            params.set(p[0], p[1]);
        }
        redirectWithParams = redirectWithParams.substring(0, redirectWithParams.indexOf('?'));
    }
    return { params, redirect: redirectWithParams };
};

const fetchAndUpdateParams = async <T>(params: URLSearchParams): Promise<T> => {
    if (Array.from(params).length < 1) { 
        return {} as T; 
    }
    try {
        let url = '/services/paramRemapper';
        for (const [name, value] of params) {
            url = `${url}.${name}=${value}`;
        }
        return await (await fetch(url)).json() as T;
    } catch (error) {
        console.error('Unable to update parameter: ' + error);
        return {} as T;
    }
};

export const handlePartnerRedirect = async function (redirectUrl?: any, errorCode?: string) {
    if (window.location.host.includes('4502')) { 
        return;
    }
    
    if (!redirectUrl) {
        redirectUrl = {
            [AcquisitionFlow.PqOfOne]: getDataEntryPages().pqOfOneDataEntryPageUrl,
            [AcquisitionFlow.PHA]: getDataEntryPages().partnerHostedDataEntryPageUrl,
        }[getAcquisitionFlow()];
    }

    const { params, redirect: partnerHostedRedirect } = extractAndMergeParams(redirectUrl);

    let sourceId = params.get('C1BSourceID');
    if (!sourceId || sourceId === 'undefined') {
        sourceId = {
            [AcquisitionFlow.PqOfOne]: 'AMEX_PO_99',
            [AcquisitionFlow.PHA]: 'C1OS',
        }[getAcquisitionFlow()];
    }

    if (errorCode) {
        params.set('errorCode', errorCode);
    }

    const updateParams = await fetchAndUpdateParams<{toSourceId?: string}>(params);
    if (updateParams?.toSourceId) {
        params.set('C1BSourceID', updateParams.toSourceId);
    }
    params.set('C1BSpecificationID', sourceId);

    // Array used below for compatibility with Node 16. 
    // TODO: If Node is upgraded to >19.8, Array can be removed and params.size can be used instead.
    window.location.assign(`${partnerHostedRedirect}${Array.from(params).length > 0 ? `?${params.toString()}` : ''}`); 
    
};

export const clearSessionAndRedirectToDataEntryPage = function (issuePushdown?: string, dataEntryPathOverride?: string, message?: string) {
    let dataEntryPageUrl = getDataEntryPages().contextPath;

    clearSessionStorageVariablesForAcquisitionFlow();
    const currentFlow = getAcquisitionFlow();
    if (dataEntryPathOverride) {
        dataEntryPageUrl = dataEntryPathOverride;
    } else {
        const URLMap = {
            [AcquisitionFlow.PA]: getPreApprovalDataEntryPage(),
            [AcquisitionFlow.PQ]: getDataEntryPages().preQualDataEntryPageUrl,
            [AcquisitionFlow.PqOfOne]: getDataEntryPages().pqOfOneDataEntryPageUrl,
            [AcquisitionFlow.SkipPQ]: getDataEntryPages().skipPreQualDataEntryPageUrl,
            [AcquisitionFlow.PHA]: getDataEntryPages().partnerHostedDataEntryPageUrl,
        };
        dataEntryPageUrl += URLMap[currentFlow];
    }

    const params = new URLSearchParams(window.location.search);
    if (message) {
        console.error(message);
    }
    if (issuePushdown) {
        params.set('errorCode', issuePushdown);
    }
    if (params.toString().length > 0) {
        dataEntryPageUrl += `?${params.toString()}`;
    }
    
    if (currentFlow === AcquisitionFlow.PqOfOne || currentFlow === AcquisitionFlow.PHA) {
        handlePartnerRedirect(cleanInternalLinkUrl(dataEntryPageUrl), issuePushdown);
    } else {
        window.location.assign(cleanInternalLinkUrl(dataEntryPageUrl));
    }
};

function getPreApprovalDataEntryPage() {
    const paDataEntryUrl = getDataEntryPages().preApprovalDataEntryPageUrl;
    const currDataEntry = regexValidation().indexPageName.test(sessionStorage.getItem('deIndexType') as string)
        ? sessionStorage.getItem('deIndexType')
        : 'index';

    return currDataEntry === 'index' ? paDataEntryUrl : paDataEntryUrl.replace('index', currDataEntry as string);
}

export const getQueryParams = () => {
    return document.location.search;
};

export const withQueryParams = (url: string) => {
    const queryParams = getQueryParams();
    return queryParams.length ? url + queryParams : url;
};

export const isActiveSession = () => {
    return sessionStorage.getItem('initResponse') ? true : false;
};

export const formatFirstAndLastName = (input: string, value: string, isBlurred?: boolean) => {
    const whatInput = input.toLowerCase().includes('first') ? 'first' : 'last';

    let format = '';
    const isAposthropheRepeated = value.match(/'/g)?.length === 2 && value.substring(value.length - 1) === '\'';

    if (isAposthropheRepeated) {
        format = value.substring(0, value.length - 1);
    } else {
        if (whatInput === 'last') {
            format = value.replace(/[^a-zA-Z-'\s]/g, '')
                .replace(/^'/g, '')
                .replace(/'+/g, '\'');
        } else {
            format = value
                .replace(/[^a-zA-Z-\s]/g, '');
        }
        format = format
            .replace(/^-/g, '')
            .replace(/-{2,}/g, '-')
            .replace(/\s{2,}/g, ' ');
    }

    if (isBlurred) {
        format = format.trim();
    }

    return format;
};

//this is passed to isValid prop of inputBase to add additional validation.
export const validateFirstAndLastName = (value: Readonly<string>) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
        return false;
    }
    
    // invalid number of apostrophes
    if (((trimmedValue.match(/'/g) || []).length ?? 0) > 1) {
        return false;
    }

    // invalid first or last char
    if (trimmedValue.startsWith('-') || trimmedValue.startsWith('\'') || trimmedValue.endsWith('-') || trimmedValue.endsWith('\'')) {
        return false;
    }

    // invalid consecutive chars
    if (/(-'|'-)/.test(trimmedValue) || /--/g.test(trimmedValue)) {
        return false
    }
    
    return true;
};

export const isValidEmailStatus = (status: string): boolean => {
    switch (status){
    case 'unknown':
    case 'valid':
    case 'accept_all':
        return true;
    default: 
        return false;
    }
};

export const getUserFirstNameFromSessionStorage = async function () {
    const pageFlowInfo = getAcquisitionFlow();
    if (pageFlowInfo === AcquisitionFlow.PQ && sessionStorage.getItem('personalInfo')) {
        const personalInfo = await SessionStorage.getCipher('personalInfo', true);
        return JSON.parse(personalInfo).FirstName;
    } else if (pageFlowInfo === AcquisitionFlow.PA && sessionStorage.getItem('pre-approval-data')) {
        const applicantData = await SessionStorage.getCipher('pre-approval-data', true);
        return applicantData.Applicant.ApplicantName.FirstName;
    }
    return '';
};

//bandaid for dob to check if date is valid for an existing bug where
// returns valid state for incorrect date through the valid prop
//  Intended For values like 02/29/1999 - or any not leap year with month February and day 29th.
//   new Date will return the following day (March 1) for this scenarios. 
//   Comparing the day received with the new day obtained we can determin if the date is valid or not.


export const isDateValid = (date: string, stateOrAuthorized?: string, setValid?: (val: boolean) => void): boolean => {
    if (date.length === 10) {
        //bandaid- CL fixed month edits not erroing but still need to validate for loqate
        //dropdown changing the state or saving when only 1 digit
        let minAge = 18;
        const maxAge = 100;
        if (stateOrAuthorized === 'au') {
            minAge = 15;
        }

        if (calculateAge(date) < minAge || calculateAge(date) > maxAge) {
            if (setValid) {
                setValid(false);
            }
            return false;
        }
        const newDate = new Date(date);
        const splitDate = date.split('/');
        return Number(splitDate[1]) === newDate.getDate();
    }
    return false;

};
//Bandaid3 for when only 1 digit is entered and we go onBlur we don't get the 'whole' date because
// onChange happens after to add the 0 to the left of the digit
export const getFormattedDate = (dob: Record<string, any>) => {

    const month = 
        dob.month.value !== undefined 
        && dob.month.value.length === 2
            ? dob.month.value
            : `0${dob.month.value}`;
    const day = 
        dob.day.value !== undefined 
        && dob.day.value.length === 2
            ? dob.day.value
            : `0${dob.day.value}`;
    const year = dob.year.value;
    const date = `${month}/${day}/${year}`;
    if (date === '0/0/') {
        return ''
    } else {
        return date;
    }
};

export const setServerTimeDiff = async (): Promise<void> => {
    if (
        SessionStorage.verifyNotEmpty('C1B-Serv-Time-Diff') &&
        !isNaN(parseInt(SessionStorage.get('C1B-Serv-Time-Diff') as string))
    ) {
        return;
    }
    try {
        const response = await axios.get('/services/v2/timestamp', {
            headers: {
                'C1B-Timestamp': new Date().getTime().toString(),
            },
        });
        if (response.status !== 200 || !response.data || !response.data.timeDiff) {
            throw response;
        }
        SessionStorage.set('C1B-Serv-Time-Diff', response.data.timeDiff);
        return;
    } catch (e) {
        console.debug('Error setting timestamp difference between server and client', e);
        SessionStorage.set('C1B-Serv-Time-Diff', '0');
        return;
    }
};

export const getCardImageName = (imgUrl: string) => {
    const splitUrl = imgUrl.split('/');
    const image = splitUrl[splitUrl.length - 1];
    const imageNoExtension = image.split('.')[0];
    return (imageNoExtension);
};

export const submitConsent = async (flowApplicationData) => {
    const appId = flowApplicationData.application.applicationId;

    try {
        await callAcquisitionBff('consent-management', {
            replace: { 'applicationId': appId },
            body: {
                referenceId: appId,
                referenceType: 'CREDIT_CARD_APPLICATION',
                consentType: 'ESIGN_AGREEMENT',
                consentMethod: 'WEB_AGREEMENT',
                consentAgreed: flowApplicationData.AgreedToFullESign ?? false,
            },
        });
        return true;
    } catch (error) {
        console.error('Consent management error:', error);
        return false;
    }
};

export const getBriteVerifyPhoneValidationResult = (): TBriteVerifyResponse | 'skipped' | false => {
    const storedValue = SessionStorage.get('bvrslt');
    try {
        if (storedValue) {
            return JSON.parse(atob(storedValue));
        }
    } catch (error) {
        console.log('bvrslt was invalid');
    }
    return false;
};

export const getIpAddress = async (): Promise<string> => {
    let ipAddress = SessionStorage.tryGet('protocol', '');

    if (!ipAddress) {
        ipAddress = await fetch('https://api64.ipify.org')
            .then((response) => {
                return response.text();
            })
            .then((data) => {
                sessionStorage.setItem('protocol', data);
                return data;
            })
            .catch((err) => {
                console.error(err);
                return '';
            });
    }

    return ipAddress;
};

export const stateMap = new Map([
    [ 'alabama',  'AL' ],
    [ 'alaska',  'AK' ],
    [ 'arizona', 'AZ' ],
    [ 'arkansas', 'AR' ],
    [  'california', 'CA' ],
    [  'colorado',  'CO' ],
    [  'connecticut',  'CT'],
    [  'dc - washington dc',  'DC' ],
    [  'district of columbia', 'DC'],
    [  'delaware',  'DE' ],
    [  'florida',  'FL' ],
    [  'georgia',  'GA' ],
    [  'hawaii',  'HI' ],
    [  'idaho',  'ID' ],
    [  'illinois',  'IL' ],
    [  'indiana',  'IN' ],
    [  'iowa',  'IA' ],
    [  'kansas',  'KS'],
    [  'kentucky',  'KY' ],
    [  'louisiana',  'LA' ],
    [  'maine',  'ME' ],
    [  'maryland',  'MD' ],
    [  'massachusetts',  'MA' ],
    [  'michigan',  'MI' ],
    [  'minnesota',  'MN' ],
    [  'mississippi',  'MS' ],
    [  'missouri',  'MO' ],
    [  'montana',  'MT' ],
    [  'nebraska',  'NE' ],
    [  'nevada',  'NV' ],
    [  'new hampshire',  'NH' ],
    [  'new jersey',  'NJ' ],
    [  'new mexico',  'NM' ],
    [  'new york',  'NY' ],
    [  'north carolina',  'NC' ],
    [  'north dakota',  'ND' ],
    [  'ohio',  'OH' ],
    [  'oklahoma',  'OK' ],
    [  'oregon',  'OR' ],
    [  'pennsylvania',  'PA' ],
    [  'rhode island',  'RI' ],
    [  'south carolina',  'SC' ],
    [  'south dakota',  'SD' ],
    [  'tennessee',  'TN' ],
    [  'texas',  'TX' ],
    [  'utah',  'UT' ],
    [  'vermont',  'VT' ],
    [  'virginia',  'VA' ],
    [  'washington',  'WA' ],
    [  'west Virginia',  'WV' ],
    [  'wisconsin',  'WI' ],
    [  'wyoming',  'WY' ],
]);

export const initializePQSetup = async (author: boolean): Promise<void> => {
    /**
     * unknown if necessary, happens in legacy
     */
    SessionStorage.removeItems([
        'isSkipPreQual',
        'isSkipPreCounterOffer',
        'personalInfo',
        'prequalFullAppData',
        'pqFullAppCheckedData',
        'prequalResponse',
    ]);

    if (!SessionStorage.get('adobeMode')) {
        acquisitionApiRequest<AcquisitionInitResponse>('init-compare-api', {})
            .then(initResponse => {
                if (initResponse.data.PQMaintenanceMode) {
                    window.location.assign(`${author ? '/content/cob-corp-acquisition/us/en' : ''}/pre-qualification/data-entry/updating-systems`);
                    return;
                }
                SessionStorage.set(
                    'initResponse',
                    JSON.stringify(initResponse.data),
                );
            })
            .catch(console.error);
    }
};

export const fullValidateRegex = (key: keyof ReturnType<typeof regexValidation>['allow' | 'disallow'], value: string, regex = regexValidation()): boolean => {
    if (!value) {
        return false;
    }
    return (regex.allow[key].test(value) && !regex.disallow[key].test(value))
};

/**
 * Intended to be used with FunctionPipe Validate
 * 
 * @param stateToValidate 
 * @returns 
 */
export const isSamePersonCheckFromFacade = async (stateToValidate: TAcquisitionProviderFacade): Promise<ValidationResult> => {
    const currentAuthorisedUserInfo = stateToValidate.getAuthorizedUser();

    const acquisitionData = stateToValidate.getPersonalInformation();

    const { authorizedUserInfo } = currentAuthorisedUserInfo;

    let applicantFirstName = acquisitionData.firstName.value;
    let applicantLastName = acquisitionData.lastName.value;
    let applicantDOB = `${
        acquisitionData.dateOfBirthMonth.value
        }/${
        acquisitionData.dateOfBirthDay.value
        }/${
        acquisitionData.dateOfBirthYear.value
        }`;

    // Applicant Info is Missing, attempt to substitute Flow Specific App Data(?)
    if (!applicantFirstName || applicantLastName || applicantDOB) {
        [applicantFirstName, applicantLastName, applicantDOB] =
            await getNameAndDOBFromSessionStorage(true);
    }
    // Applicant Info is Missing, attempt to substitute from Counter Applicant info
    if (SessionStorage.verifyNotEmpty('personalInfo')) {
        try {
            const personalInfo = await SessionStorage.getCipher<{
                FirstName: string,
                LastName: string,
                DOBMonth: string,
                DOBDay: string,
                DOBYear: string,
            }>('personalInfo', true);

            applicantFirstName = personalInfo.FirstName;
            applicantLastName = personalInfo.LastName;
            applicantDOB =
                `${
                personalInfo.DOBMonth
                }/${
                personalInfo.DOBDay
                }/${
                personalInfo.DOBYear
                }`;
        } catch (_) {}
    }

    if (
        !applicantFirstName
        || !applicantLastName
        || !applicantDOB
        || !authorizedUserInfo.AuthorizedUserFirstName
        || !authorizedUserInfo.AuthorizedUserLastName
        || !authorizedUserInfo.AuthorizedUserDOBDay
        || !authorizedUserInfo.AuthorizedUserDOBMonth
        || !authorizedUserInfo.AuthorizedUserDOBYear
    ) {
        return { valid: true };
    }

    let authorizedUserDob = `${
        authorizedUserInfo.AuthorizedUserDOBMonth
        }/${
        authorizedUserInfo.AuthorizedUserDOBDay
        }/${
        authorizedUserInfo.AuthorizedUserDOBYear
        }`;

    const hashApplicant = `${applicantFirstName},${applicantLastName},${applicantDOB}`.toLowerCase();
    const hashAuthorisedUser = `${
        authorizedUserInfo.AuthorizedUserFirstName
        },${
        authorizedUserInfo.AuthorizedUserLastName
        },${
        authorizedUserDob
        }`.toLowerCase();

    const isSamePerson = hashApplicant === hashAuthorisedUser;
    return { valid: !isSamePerson };
};

export const isSamePersonCheckFromFacadePipe = async (stateToValidate: TAcquisitionProviderFacade) => 
    await FunctionPipe.pipe(stateToValidate).validate(isSamePersonCheckFromFacade);



/**
 * Intended to be used in a FunctionPipe Validate() step
 * 
 * @param stateToValidate 
 * @return
 */
export const validatePhoneNumberFromFacade = async (stateToValidate: TAcquisitionProviderFacade): Promise<ValidationResult> => {
    const { isAdobeMode, isAuthor, usePhoneApi, phoneValidationApi, regexValidation } = stateToValidate.getCurrentMetaAcquisitionFlowState();

    const valueToValidate = 
        stateToValidate.getPersonalInformation().phoneNumber.value 
        || stateToValidate.getPersonalInfoForm().phoneNumber.value
        || stateToValidate.getPhoneAndIncomeInfo().phoneNumber?.value;

    // Static Validations
    if (
        !valueToValidate
        || valueToValidate.replaceAll(/\D/g, '').length !== 10
        || !fullValidateRegex('phone', valueToValidate, regexValidation)
    ) {
        return { valid: false }
    }

    // Bypass validation
    if (
        isAuthor
        || (isAdobeMode && !usePhoneApi)
    ) {
        return { valid: true };
    }


    // Dynamic Validations
    if (usePhoneApi) {
        const result = await phoneValidationApi(valueToValidate);
        return { valid: !!result }
    }

    return { valid: true };
};

/**
 * Intended to be used in a FunctionPipe Validate() step
 * 
 * @param stateToValidate 
 * @returns 
 */
export const validateEmailAddressFromFacade = async (stateToValidate: TAcquisitionProviderFacade): Promise<ValidationResult> => {
    const { flow, isAdobeMode, isAuthor, useEmailApi, emailValidationApi, regexValidation } = stateToValidate.getCurrentMetaAcquisitionFlowState();

    const valueToValidate = stateToValidate.getPersonalInformation().emailAddress?.value || stateToValidate.getPersonalInfoForm().email.value;

    // Static Validations
    if (
        !valueToValidate
        || valueToValidate.indexOf('@') === -1
        || !fullValidateRegex('email', valueToValidate, regexValidation)
    ) {
        return { valid: false };
    }

    // Bypass Validation
    if (
        !isAuthor
        || flow === AcquisitionFlow.PqOfOne
        || (isAdobeMode && !useEmailApi)
    ) {
        return { valid: true };
    }

    // Dynamic Validations
    if (useEmailApi) {
        const result = await emailValidationApi(valueToValidate);
        return { valid: !!result };
    }

    return { valid: true };
};